import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Paper,
} from '@mui/material';
import {
  Summarize as SummaryIcon,
  TrendingUp as IngresoIcon,
  TrendingDown as EgresoIcon,
} from '@mui/icons-material';
import { PosCajaService } from '../services/posCajaService';
import { usePosStore } from '../store/posStore';
import { formatCurrency } from '../utils/posCalculations';
import type { ResumenCajaResponse } from '../types/pos.types';

interface CashSummaryDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CashSummaryDialog: React.FC<CashSummaryDialogProps> = ({ open, onClose }) => {
  const { sesionActiva } = usePosStore();
  const [resumen, setResumen] = useState<ResumenCajaResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      PosCajaService.getResumen()
        .then(setResumen)
        .catch(() => setResumen(null))
        .finally(() => setLoading(false));
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SummaryIcon color="primary" />
          Resumen de Caja
          {sesionActiva && (
            <Chip
              label={sesionActiva.cajaNombre ?? `Caja #${sesionActiva.idCajaBanco}`}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : resumen ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Summary cards */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Paper variant="outlined" sx={{ flex: 1, minWidth: 140, p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Apertura</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(resumen.montoApertura)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ flex: 1, minWidth: 140, p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Ventas ({resumen.cantidadVentas})
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  +{formatCurrency(resumen.totalVentas)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ flex: 1, minWidth: 140, p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Ingresos ({resumen.cantidadIngresos})
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="success.main">
                  +{formatCurrency(resumen.totalIngresos)}
                </Typography>
              </Paper>
              <Paper variant="outlined" sx={{ flex: 1, minWidth: 140, p: 1.5, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Egresos ({resumen.cantidadEgresos})
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="error.main">
                  -{formatCurrency(resumen.totalEgresos)}
                </Typography>
              </Paper>
            </Box>

            {/* Expected total */}
            <Paper
              variant="outlined"
              sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.50', borderColor: 'primary.main' }}
            >
              <Typography variant="body2" color="text.secondary">Monto Esperado en Caja</Typography>
              <Typography variant="h4" fontWeight="bold" color="primary">
                {formatCurrency(resumen.montoEsperado)}
              </Typography>
            </Paper>

            {/* Movements table */}
            <Divider />
            <Typography variant="subtitle1" fontWeight="bold">
              Movimientos ({resumen.movimientos.length})
            </Typography>

            {resumen.movimientos.length > 0 ? (
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Concepto</TableCell>
                      <TableCell>Descripcion</TableCell>
                      <TableCell>Usuario</TableCell>
                      <TableCell>Hora</TableCell>
                      <TableCell align="right">Monto</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resumen.movimientos.map((mov) => (
                      <TableRow key={mov.idMovimientoCaja}>
                        <TableCell>
                          <Chip
                            icon={mov.tipo === 'INGRESO' ? <IngresoIcon /> : <EgresoIcon />}
                            label={mov.tipo}
                            size="small"
                            color={mov.tipo === 'INGRESO' ? 'success' : 'error'}
                            variant="outlined"
                            sx={{ height: 24 }}
                          />
                        </TableCell>
                        <TableCell>{mov.concepto}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {mov.descripcion || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>{mov.usuarioNombre || '-'}</TableCell>
                        <TableCell>
                          {new Date(mov.fechaMovimiento).toLocaleTimeString('es-PE', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            fontWeight="bold"
                            color={mov.tipo === 'INGRESO' ? 'success.main' : 'error.main'}
                          >
                            {mov.tipo === 'INGRESO' ? '+' : '-'}{formatCurrency(mov.monto)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" textAlign="center" sx={{ py: 2 }}>
                No hay movimientos manuales registrados
              </Typography>
            )}
          </Box>
        ) : (
          <Typography color="text.secondary" textAlign="center" sx={{ py: 4 }}>
            No se pudo cargar el resumen
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CashSummaryDialog;
